import React, { useState } from 'react';

import { styled } from '@asicsdigital/oneasics-blocks';
import PropTypes from 'prop-types';
import { useTracking } from 'react-tracking';

export const Accordion = styled('div', {
  background: 'white',
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingBottom: '5px',
  textAlign: 'left',
  width: '345px',

  '> div': {
    borderBottom: '1px solid $grey300',
    paddingLeft: '5px',
    paddingRight: '5px',

    '&:first-of-type': {
      borderTop: '1px solid $grey300',
    },
  },

  '@tablet': {
    margin: 'unset',
    width: '440px',

    '& a': {
        textDecoration: 'none',
    },

    '& a:hover': {
        textDecoration: 'underline',
    },
  },
});

const AccordionRowTitleWrapper = styled('div', {
  alignItems: 'center',
  display: 'flex',
  height: '30px',
  padding: '7px',

  ':focus': {
    outline: 'none',
  },

  ':hover': {
    cursor: 'pointer',
  },
});

const AccordionRowTitle = styled('div', {
  flex: '1',
  fontSize: '15px',
  fontWeight: '$bold',

  ':focus': {
    outline: 'none',
  },

  '@tablet': {
    fontSize: '$large',
  },
});

const AccordionRowOpenCloseIcon = styled('div', {
  fontSize: '28px',
});

const AccordionRowContent = styled('div', {
  display: 'none',
  marginLeft: 'auto',
  marginRight: 'auto',
  padding: '7px',

  variants: {
    open: {
      true: {
        display: 'block',
      },
      false: {
        display: 'none',
      },
    },
  },
});

/**
 * A row to be used in an <Accordion> defined at the top of the file.
 */
const AccordionRow = ({ children, name, rowTitle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { trackEvent } = useTracking();

  const toggleAccordionRow = () => {
    // TODO: add analytics trackEvent({ event: `accordion-${name}-toggle`, type: 'click' });
    setIsOpen(!isOpen);
    if(!isOpen) {
      trackEvent({ 
        gaEventAction: `accordion-${name}-toggle`, 
        gaEventLabel: `${rowTitle}`,
        gaEventNonInteraction: false,
      });
    }
  };

  const handleKeyClick = (event) => {
    if(event.key === ' ' || event.code === 'Space' || event.keyCode === '32' || event.key === 'Enter') {
      event.preventDefault();
      toggleAccordionRow();
    }
  };

  return (
    <div>
      <AccordionRowTitleWrapper
        id={`accordion-row-${name}`}
        role="button"
        tabIndex="0"
        onClick={toggleAccordionRow}
        onKeyDown={handleKeyClick}
      >
        <AccordionRowTitle>{rowTitle}</AccordionRowTitle>
        <AccordionRowOpenCloseIcon>{isOpen ?  '-' : '+'}</AccordionRowOpenCloseIcon>
      </AccordionRowTitleWrapper>
      <AccordionRowContent open={isOpen}>
        {children}
      </AccordionRowContent>
    </div>
  );
};

AccordionRow.propTypes = {
  /**
   * The content of the accordion row.
   */
  children: PropTypes.node.isRequired,
  /**
   * The name of the accordion row.
   */
  name: PropTypes.string.isRequired,
  /**
   * The title used in the accordion row.
   */
  rowTitle: PropTypes.string.isRequired,
};

AccordionRow.defaultProps = {};

export default AccordionRow;
